import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useLocation } from 'react-router'
import {
  NOT_TRACKED,
  routeRegex,
  RouteType,
} from '@/resources/enums/enumRoutes'

interface Props {
  children: NonNullable<ReactNode>
}

export interface RouterStore {
  previousPage: string | null
  currentPage: string | null
}

const RouterStoreContext = createContext<RouterStore>({
  previousPage: NOT_TRACKED,
  currentPage: NOT_TRACKED,
})

const RouterStoreProvider = ({ children }: Props) => {
  const [pageState, setPageState] = useState<RouterStore>({
    previousPage: null,
    currentPage: null,
  })

  const { pathname } = useLocation()

  const getPathNameKey = () => {
    let path = pathname

    if (path[path.length - 1] === '/') {
      path = path.substring(0, path.length - 2)
    }
    if (routeRegex.test(path)) {
      path = path.replace(/\d+/, '%s')
    }
    if (Object.keys(RouteType).includes(path)) {
      return RouteType[path as keyof typeof RouteType]
    }
    return NOT_TRACKED
  }

  const pathnameKey = getPathNameKey()

  useEffect(() => {
    if (pageState.currentPage === pathnameKey) return
    setPageState(prevState => ({
      previousPage: prevState.currentPage,
      currentPage: pathnameKey,
    }))
  }, [pathnameKey, pageState])

  return (
    <RouterStoreContext.Provider
      value={{
        previousPage: pageState.previousPage,
        currentPage: pageState.currentPage,
      }}
    >
      {children}
    </RouterStoreContext.Provider>
  )
}

export const useRouterStore = () => {
  const context = useContext(RouterStoreContext)

  if (!context) {
    throw new Error('useRouterStore must be used within a RouterStoreProvider')
  }

  return context
}

export default RouterStoreProvider
